import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box } from '@mui/material';
import dayjs from 'dayjs';
import { transformChartData } from '../../utils/apiCommonMethods';
import { fetchSiteStatusChartData, getSolarOnlyChartData } from '../../utils/chartAPI';
import BatteryPowerChart from './BatteryPowerChart';
import StateOfChargeChart from './StateOfChargeChart';
import GridOutputChart from './GridOutputChart';
import { allowedSolarOnlySystemIds } from '../../apiConfig';

SiteStatusCharts.propTypes = {
  site: PropTypes.object.isRequired,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  msSessionInfo: PropTypes.object.isRequired,
  refreshKey: PropTypes.number,
};

export default function SiteStatusCharts({
  site,
  startDate,
  endDate,
  msSessionInfo,
  refreshKey,
}) {
  const [seriesPower, setSeriesPower] = useState([]);
  const [seriesSoc, setSeriesSoc] = useState([]);
  const [seriesPoi, setSeriesPoi] = useState([]);

  const [hasAnyPower, setHasAnyPower] = useState(false);
  const [hasAnySoc, setHasAnySoc] = useState(false);
  const [hasAnyPoi, setHasAnyPoi] = useState(false);

  const [loading, setLoading] = useState(true);
  const [expandedChart, setExpandedChart] = useState(null);
  const isSolarOnly = allowedSolarOnlySystemIds.has(site?.id);
  
  const handleExpandContract = (key) => {
    setExpandedChart((prev) => (prev === key ? null : key));
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  };

  const clearChartState = () => {
    setSeriesPower([]);
    setSeriesSoc([]);
    setSeriesPoi([]);
    setHasAnyPower(false);
    setHasAnySoc(false);
    setHasAnyPoi(false);
  };

  const toMs = (t) => (typeof t === 'number' ? t : dayjs(t).valueOf());

  const pairXY = (labelsArr = [], valuesArr = []) =>
    labelsArr.map((lbl, i) => ({ x: toMs(lbl), y: valuesArr[i] }));

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);

      try {
        const fetchFn = isSolarOnly ? getSolarOnlyChartData : fetchSiteStatusChartData;
        const data = (await fetchFn(site, startDate, endDate, msSessionInfo)) ?? {};

        const clean = transformChartData({ tsStatus: data });

        // POWER (solar output for solar-only sites)
        const powerTs = isSolarOnly ? clean?.solar : clean?.power;
        if (powerTs?.labels?.length && powerTs?.dataSet?.length) {
          setSeriesPower([
            {
              name: isSolarOnly ? 'Solar Output' : 'Power',
              type: 'area',
              data: pairXY(powerTs.labels, powerTs.dataSet),
            },
          ]);
          setHasAnyPower(true);
        } else {
          setSeriesPower([]);
          setHasAnyPower(false);
        }

        // SOC
        if (clean?.soc?.labels?.length && clean?.soc?.dataSet?.length) {
          setSeriesSoc([
            {
              name: 'SOC %',
              type: 'area',
              data: pairXY(clean.soc.labels, clean.soc.dataSet),
            },
          ]);
          setHasAnySoc(true);
        } else {
          setSeriesSoc([]);
          setHasAnySoc(false);
        }

        // POI
        const hasPoiLabels = clean?.pvToGrid?.labels?.length;
        const poiSeries = [];

        if (hasPoiLabels) {
          if (clean?.pvToGrid?.dataSet?.length) {
            poiSeries.push({
              name: 'Grid Output',
              type: 'area',
              data: pairXY(clean.pvToGrid.labels, clean.pvToGrid.dataSet),
            });
          }

          if (clean?.bessImport?.dataSet?.length) {
            poiSeries.push({
              name: 'Battery Charge',
              type: 'area',
              data: pairXY(clean.pvToGrid.labels, clean.bessImport.dataSet),
            });
          }

          if (clean?.bessExport?.dataSet?.length) {
            poiSeries.push({
              name: 'Battery Discharge',
              type: 'area',
              data: pairXY(clean.pvToGrid.labels, clean.bessExport.dataSet),
            });
          }
        }

        setSeriesPoi(poiSeries);
        setHasAnyPoi(poiSeries.length > 0);
      } catch (error) {
        console.error('Failed to fetch site status chart data:', error);
        clearChartState();
      } finally {
        setLoading(false);
      }
    };

    if (site?.id && msSessionInfo && startDate && endDate) {
      fetchChartData();
    } else {
      setLoading(false);
      clearChartState();
    }
  }, [
    site?.id,
    startDate?.valueOf?.(),
    endDate?.valueOf?.(),
    msSessionInfo,
    refreshKey,
  ]);

  const containerMb = loading || hasAnyPoi ? 0 : (theme) => `-${theme.spacing(3)}`;

  return (
    <Box sx={{ mb: containerMb }}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid
          item
          xs={12}
          md={isSolarOnly || expandedChart === 'power' ? 12 : 6}
          sx={{ display: expandedChart === 'soc' ? 'none' : 'block' }}
        >
          <BatteryPowerChart
            site={site}
            title={isSolarOnly ? 'Solar Output' : undefined}
            emptyText={isSolarOnly ? 'No solar output data available.' : undefined}
            startDate={startDate}
            endDate={endDate}
            series={seriesPower}
            loading={loading}
            hasData={hasAnyPower}
            hidden={expandedChart === 'soc'}
            onToggleExpand={() => handleExpandContract('power')}
          />
        </Grid>

      {!isSolarOnly && (
        <Grid
          item
          xs={12}
          md={expandedChart === 'soc' ? 12 : 6}
          sx={{ display: expandedChart === 'power' ? 'none' : 'block' }}
        >
          <StateOfChargeChart
            startDate={startDate}
            endDate={endDate}
            series={seriesSoc}
            loading={loading}
            hasData={hasAnySoc}
            hidden={expandedChart === 'power'}
            onToggleExpand={() => handleExpandContract('soc')}
          />
        </Grid>
      )}
      </Grid>

      {!isSolarOnly && (loading || hasAnyPoi) && (
        <Grid container spacing={3} mt={0.1}>
          <Grid item xs={12}>
            <GridOutputChart
              startDate={startDate}
              endDate={endDate}
              series={seriesPoi}
              loading={loading}
              hasData={hasAnyPoi}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}